import React from 'react'
import memoize from 'memoize-one'

import { RecurlyElementsContext } from '../elements'

const noop = () => ({})
const extractOptions = (props) => {
  const {
    id: _id,
    className: _className,
    onChange: _onChange,
    onBlur: _onBlur,
    onFocus: _onFocus,
    onReady: _onReady,
    onSubmit: _onSubmit,
    onCoBadge: _onCoBadge,
    ...options
  } = props
  return options
}

/**
 * An [Element](https://developers.recurly.com/reference/recurly-js/#elements)
 * component which wraps its Recurly.js analogue, passing configuration props to the underlying
 * Recurly.js Element and allowing event binding using props.
 */
export default class Element extends React.PureComponent {
  configureElement = memoize(
    (element, options) =>
      element && Object.keys(options).length > 0 && element.configure(options),
  )

  constructor(props, context) {
    super(props, context)
    const { elementClassName } = this.constructor

    if (!context || !context.elements) {
      throw new Error(
        `<${elementClassName}> must be within an <Elements> tree.`,
      )
    }

    this.container = React.createRef()
  }

  componentDidMount() {
    const { elementClassName } = this.constructor
    const options = extractOptions(this.props)
    const {
      onReady = noop,
      onChange = noop,
      onBlur = noop,
      onFocus = noop,
      onSubmit = noop,
      onCoBadge = noop,
    } = this.props
    const { elements } = this.context
    this.element = elements[elementClassName](options)

    this.element.on('attach', (...args) => onReady(...args))
    this.element.on('change', (...args) => onChange(...args))
    this.element.on('blur', (...args) => onBlur(...args))
    this.element.on('focus', (...args) => onFocus(...args))
    this.element.on('submit', (...args) => onSubmit(...args))
    this.element.on('coBadge', (...args) => onCoBadge(...args))

    this.element.attach(this.container.current)
  }

  componentWillUnmount() {
    this.element.destroy()
  }

  render() {
    const { element } = this
    const { id, className } = this.props

    this.configureElement(element, extractOptions(this.props))

    return <div id={id} className={className} ref={this.container} />
  }
}

Element.contextType = RecurlyElementsContext
